import React from 'react';

export const INDICATOR_COLORS = ['#d29922', '#00d4aa', '#ec4899', '#ad93e9', '#06b6d4', '#f85149'];

// BUY/SELL are the long lifecycle, SHORT/COVER are the short lifecycle -- kept as
// distinct colors (not just green/red) so the two are never confusable.
export const TRADE_ACTION_COLORS = { BUY: '#26a641', SELL: '#f85149', SHORT: '#d29922', COVER: '#00d4aa' };

// Intraday bars are stored as UTC ISO strings ('2023-01-01T22:00:00').
// Convert to the browser's local timezone so times make intuitive sense.
export function fmtBarDate(d) {
  if (!d || typeof d !== 'string') return d || '';
  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  if (d.includes('T')) {
    const local = new Date(d + 'Z'); // 'Z' = parse as UTC
    const dow = DAYS[local.getDay()];
    const m = String(local.getMonth() + 1).padStart(2, '0');
    const day = String(local.getDate()).padStart(2, '0');
    const h = String(local.getHours()).padStart(2, '0');
    const min = String(local.getMinutes()).padStart(2, '0');
    return `${dow} ${m}/${day} ${h}:${min}`;
  }
  const parts = d.split('-');
  return parts.length >= 3 ? `${parts[1]}/${parts[2]}` : d;
}

/**
 * Returns the chart's effective bar interval by taking the minimum consecutive
 * gap across the first 20 bars. This skips weekends/overnight gaps which would
 * otherwise inflate an average-based estimate.
 */
export function detectBarInterval(data) {
  if (!data || data.length < 2) return null;
  const toTs = (d) => d.includes('T')
    ? new Date(d + 'Z').getTime() / 1000
    : new Date(d + 'T00:00:00Z').getTime() / 1000;
  const n = Math.min(20, data.length - 1);
  let minGap = Infinity;
  for (let i = 0; i < n; i++) {
    const gap = toTs(data[i + 1].date) - toTs(data[i].date);
    if (gap > 0) minGap = Math.min(minGap, gap);
  }
  if (minGap === Infinity) return null;
  if (minGap < 3600)  return `${Math.round(minGap / 60)}m`;
  if (minGap < 86400) return `${Math.round(minGap / 3600)}h`;
  return `${Math.round(minGap / 86400)}d`;
}

export function fmt$(val) {
  if (val == null) return '—';
  return '$' + Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Recursively finds every numeric leaf under each node's `data` object.
// Shared by QuickEditPanel (edit any numeric param) and ParameterSweepPanel
// (pick 1-2 numeric params to sweep across a range).
export function extractNumericParams(graph) {
  const nodes = graph?.nodes || [];
  const params = [];

  const walk = (node, obj, path = []) => {
    if (!obj || typeof obj !== 'object') return;
    Object.entries(obj).forEach(([k, v]) => {
      const p = [...path, k];
      if (typeof v === 'number' && Number.isFinite(v)) {
        params.push({
          key: `${node.id}:${p.join('.')}`,
          nodeId: node.id,
          nodeType: node.type,
          nodeLabel: node.data?.label || node.type,
          nodeData: node.data || {},
          path: p,
          value: v,
        });
        return;
      }
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        walk(node, v, p);
      }
    });
  };

  nodes.forEach((node) => walk(node, node.data || {}, ['data']));
  return params.sort((a, b) => a.key.localeCompare(b.key));
}

// ---- Shared numeric-param editing helpers (used by QuickEditPanel + ComparePanel) ----

export const ACTION_TYPE_LABELS = { buy: 'Buy', sell: 'Sell', short: 'Short', cover: 'Cover', close: 'Close Position' };
export const INDICATOR_TYPE_LABELS = {
  sma: 'SMA', ema: 'EMA', rsi: 'RSI', bollinger: 'Bollinger Bands', macd: 'MACD',
  atr: 'ATR', stochastic: 'Stochastic', vwap: 'VWAP', adx: 'ADX', cci: 'CCI',
  williams_r: 'Williams %R', roc: 'ROC', momentum: 'Momentum', trix: 'TRIX',
  vortex: 'Vortex', dpo: 'DPO', supertrend: 'Supertrend',
};

// Distinct, stable palette for variants/series in comparison views.
export const VARIANT_COLORS = ['#00d4aa', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#22c55e', '#06b6d4'];

// Writes `value` into `root` at the given `path` (array of keys), creating
// intermediate objects as needed.
export function setByPath(root, path, value) {
  if (!path.length) return;
  let ref = root;
  for (let i = 0; i < path.length - 1; i += 1) {
    const key = path[i];
    if (!ref[key] || typeof ref[key] !== 'object') ref[key] = {};
    ref = ref[key];
  }
  ref[path[path.length - 1]] = value;
}

export function titleCaseWords(val) {
  return String(val || '')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function paramPathLabel(path = []) {
  return path.slice(1).join('.');
}

// Human context for a node (e.g. the action type or indicator kind) so param
// labels read naturally instead of exposing raw graph keys.
export function nodeContext(nodeType, nodeData) {
  if (nodeType === 'action') return ACTION_TYPE_LABELS[nodeData?.action] || null;
  if (nodeType === 'indicator') return INDICATOR_TYPE_LABELS[nodeData?.indicator] || null;
  if (nodeType === 'condition') return titleCaseWords(nodeData?.operator || '');
  return null;
}

export function nodeTitleFromParam(param) {
  const base = param.nodeLabel || titleCaseWords(param.nodeType || 'node');
  const context = nodeContext(param.nodeType, param.nodeData);
  return context ? `${base} (${context})` : base;
}

export function numericFieldLabel(param) {
  const leaf = param.path?.[param.path.length - 1] || '';
  if (param.nodeType === 'action' && leaf === 'size_pct') {
    const actionLabel = ACTION_TYPE_LABELS[param.nodeData?.action] || 'Action';
    return `${actionLabel} size %`;
  }
  if (param.nodeType === 'indicator' && leaf === 'period') {
    const indicatorLabel = INDICATOR_TYPE_LABELS[param.nodeData?.indicator] || 'Indicator';
    return `${indicatorLabel} period`;
  }
  return paramPathLabel(param.path)
    .split('.')
    .map((part) => titleCaseWords(part))
    .join(' > ');
}

// Returns a deep-cloned graph with the given numeric-param overrides applied.
// `values` maps param.key -> raw string/number; non-finite values are skipped.
export function applyParamOverrides(graph, params, values) {
  if (!graph) return null;
  const next = JSON.parse(JSON.stringify(graph));
  const byId = {};
  (next.nodes || []).forEach((n) => { byId[n.id] = n; });
  params.forEach((p) => {
    const node = byId[p.nodeId];
    if (!node) return;
    const parsed = Number(values[p.key]);
    if (!Number.isFinite(parsed)) return;
    setByPath(node, p.path, parsed);
  });
  return next;
}

export function StatCard({ label, value, color, emphasis = false }) {
  return (
    <div className="stat-card" style={{
      padding: emphasis ? 'var(--space-4)' : 'var(--space-3)',
      background: 'var(--card)',
      borderRadius: 'var(--radius-md)',
      border: '1px solid var(--border)',
    }}>
      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--muted)', marginBottom: 'var(--space-1)' }}>{label}</div>
      <div className="stat-value" style={{ fontSize: emphasis ? 'var(--fs-2xl)' : 'var(--fs-lg)', fontWeight: 700, fontFamily: 'var(--font-mono)', color: color || 'var(--text)' }}>{value}</div>
    </div>
  );
}
